// pwa/src/components/Chat.js
import React, { useState, useEffect, useRef, useContext } from "react";
import ReactMarkdown from "react-markdown";
import { FormattedMessage, useIntl } from "react-intl";
import { AppContext } from "../utilities/context";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AnimatedAssistantIcon from "./AnimatedAssistantIcon";
import SendIcon from "@mui/icons-material/Send";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
// import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
// import { SvgIcon } from '@mui/material';
// import Styles from "../../public/assets/css/main.css";

// const Chat = ({ shopId }) => {
const Chat = React.forwardRef(({ shopId }, ref) => {
  const intl = useIntl();
  const context = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [products, setProducts] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamText, setStreamText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [eventSource, setEventSource] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [tenantConfig, setTenantConfig] = useState(null);
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const textFieldRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    handleClickOpen,
    handleClose,
  }));

  useEffect(() => {
    if (context) {
      setTenantConfig(context.tenantConfig);
      console.log("Chat tenantConfig set from context:", context.tenantConfig);
    }
  }, [shopId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = () => {
    console.log("handleSendMessage called");
    if (isStreaming) return;
    if (newMessage.trim()) {
      // Add user message to the UI
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          chatResponse: { text: newMessage },
          sender: "user",
          timestamp: new Date().toISOString(),
        },
      ]);
      scrollToBottom();

      try {
        setLoading(true);

        // Create URL with message and conversation history
        const historyParam =
          conversationHistory.length > 0
            ? `&history=${encodeURIComponent(
                btoa(JSON.stringify(conversationHistory))
              )}`
            : "";

        const apiUrl = tenantConfig?.apiUrl || process.env.REACT_APP_API_URL;
        const newEventSource = new EventSource(
          `${apiUrl}/api/llm/search?shopId=${shopId}&message=${encodeURIComponent(
            newMessage
          )}${historyParam}`
        );

        let accumulatedResponse = "";

        newEventSource.onmessage = (event) => {
          if (event.data === "[DONE]") {
            newEventSource.close();
            setIsStreaming(false);
            setEventSource(null);
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                chatResponse: {
                  text: accumulatedResponse,
                },
                sender: "assistant",
                timestamp: new Date().toISOString(),
              },
            ]);
            scrollToBottom();
            setStreamText("");
            setLoading(false);
          } else {
            let result;
            try {
              result = JSON.parse(event.data);
              switch (result.type) {
                case "products":
                  setProducts(result.content || []);
                  break;
                case "answer_chunk":
                  accumulatedResponse += result.content;
                  setStreamText((prevText) => prevText + result.content);
                  scrollToBottom();
                  break;
                case "conversation_history":
                  // Update the conversation history from base64
                  try {
                    const decodedHistory = JSON.parse(atob(result.content));
                    setConversationHistory(decodedHistory);
                  } catch (e) {
                    console.error("Error decoding conversation history:", e);
                  }
                  break;
                case "error":
                  console.error("Error en el stream:", result.content);
                  newEventSource.close();
                  break;
              }
            } catch (e) {
              console.error("No es JSON válido", e);
            }
          }
        };

        newEventSource.onerror = (error) => {
          console.error("EventSource failed:", error);
          newEventSource.close();
          setIsStreaming(false);
          setError(error.message);
          setLoading(false);
        };

        setEventSource(newEventSource);
        setIsStreaming(true);
      } catch (error) {
        console.error("Error sending message:", error);
        setError("Error enviando mensaje");
      }
      setNewMessage("");
    }
  };

  const stopStream = () => {
    if (eventSource) {
      eventSource.close();
      setIsStreaming(false);
      setEventSource(null);

      // Add the partial response to messages
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          chatResponse: { text: streamText },
          sender: "assistant",
          timestamp: new Date().toISOString(),
        },
      ]);

      // Also update conversation history with the partial response
      setConversationHistory((prev) => [
        ...prev,
        { role: "assistant", content: streamText },
      ]);

      scrollToBottom();
      setStreamText("");
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
  };

  const renderMessageContent = (message) => {
    if (message.sender === "user") {
      return message.chatResponse.text;
    } else {
      return (
        <ReactMarkdown
          components={{
            a: ({ node, ...props }) => (
              <a
                target="_self"
                className="product-url"
                rel="noopener noreferrer"
                {...props}
              />
            ),
          }}
        >
          {message.chatResponse.text}
        </ReactMarkdown>
      );
    }
  };

  // Function to reset conversation
  const resetConversation = () => {
    setMessages([]);
    setConversationHistory([]);
    setProducts([]);
    setStreamText("");
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    setIsStreaming(false);
  };

  // const CustomAssistantIcon = (props) => {
  //   return (
  //     <SvgIcon {...props}>
  //       <path d="M19 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h4l3 3 3-3h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 16h-4.83l-.59.59L12 20.17l-1.59-1.59-.58-.58H5V4h14v14z" />
  //       <path d="M12 11c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm2.97 6.31L10 14.3l-2.97 3.01 1.42-4.28L5.5 10h3.8l1.2-4 1.2 4h3.8l-2.95 3.03 1.42 4.28z" fill="#FFEB3B" />
  //     </SvgIcon>
  //   );
  // }

  return (
    <div>
      {/* INI CHAT */}
      {!open && (
        <Fab
          color="primary"
          aria-label="chat"
          onClick={handleClickOpen}
          className="mui-fab-primary"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
          }}
        >
          <AnimatedAssistantIcon isAnimating={open} size={32} />
        </Fab>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        TransitionProps={{
          onEntered: () => {
            if (textFieldRef.current) {
              textFieldRef.current.focus();
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            <FormattedMessage id="app.chat.title" />
          </span>
          <Box>
            {/* <Tooltip title="Reset Conversation">
              <IconButton
                aria-label="Reset Conversation"
                variant="outlined"
                size="small"
                onClick={resetConversation}
                sx={{ mr: 1 }}
              >
                <CleaningServicesIcon />
              </IconButton>
            </Tooltip> */}
            <Tooltip title="Close">
              <IconButton aria-label="Close" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 0, // para aprovechar todo el espacio
          }}
        >
          <Paper
            elevation={0}
            sx={{
              flexGrow: 1,
              height: "50vh",
              overflowY: "auto",
              p: 2,
              mb: 2,
              position: "relative",
            }}
          >
            {loading && !streamText && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <CircularProgress />
              </Box>
            )}
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems:
                    message.sender === "user" ? "flex-end" : "flex-start",
                  mb: 1,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    maxWidth: "70%",
                    bgcolor:
                      message.sender === "user" ? "primary.light" : "grey.100",
                  }}
                >
                  {renderMessageContent(message)}
                </Paper>
                <Typography variant="caption" sx={{ mt: 0.5 }}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </Typography>
              </Box>
            ))}

            {/* Display currently streaming message */}
            {streamText && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    maxWidth: "70%",
                    bgcolor: "grey.100",
                  }}
                >
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          target="_self"
                          rel="noopener noreferrer"
                          className="product-url"
                          {...props}
                        />
                      ),
                    }}
                  >
                    {streamText}
                  </ReactMarkdown>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Paper>
          <Box sx={{ display: "flex", gap: 1, p: 2, alignItems: "center" }}>
            <TextField
              inputRef={textFieldRef}
              fullWidth
              variant="outlined"
              placeholder={intl.formatMessage({
                id: "app.chat.input.placeholder",
              })}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (isStreaming && streamText) {
                    stopStream();
                  } else {
                    handleSendMessage();
                  }
                }
              }}
              multiline
              rows={2}
              sx={{ alignItems: "flex-start" }}
            />
            <Tooltip
              title={
                isStreaming && streamText ? "Stop Response" : "Send Message"
              }
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": {
                    color: theme.palette.primary.dark,
                  },
                }}
              >
                {isStreaming && streamText ? (
                  <StopCircleIcon onClick={stopStream} />
                ) : (
                  <ArrowUpwardIcon onClick={handleSendMessage} />
                )}
              </Box>
            </Tooltip>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      {/* FIN CHAT */}
    </div>
  );
});

export default Chat;
